import React, { useState, useEffect } from 'react';
import { X, CheckCircle } from 'lucide-react';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import * as enrollmentService from '../../../services/enrollmentService';
import * as studentService from '../../../services/studentService';
import * as programService from '../../../services/programService';

const BulkEnrollmentModal = ({ academicYearId, semesterId, programs, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1: Setup, 2: Select Students, 3: Select Units, 4: Review & Process
  const [batchData, setBatchData] = useState({
    batchName: '',
    programId: '',
    levelId: '',
    studentIds: [],
    unitIds: []
  });
  const [students, setStudents] = useState([]);
  const [units, setUnits] = useState([]);
  const [levels, setLevels] = useState([]);
  const [processResult, setProcessResult] = useState(null);

  useEffect(() => {
    if (step === 2 && batchData?.programId) {
      loadStudents();
    }
  }, [step, batchData?.programId]);

  useEffect(() => {
    if (step === 3 && batchData?.programId && batchData?.levelId) {
      loadUnits();
    }
  }, [step, batchData?.programId, batchData?.levelId]);

  useEffect(() => {
    if (batchData?.programId) {
      loadLevels();
    }
  }, [batchData?.programId]);

  const loadLevels = async () => {
    try {
      const result = await programService?.getProgramLevels(batchData?.programId);
      if (result?.success) {
        setLevels(result?.data || []);
      }
    } catch (error) {
      console.error('Error loading levels:', error);
    }
  };

  const loadStudents = async () => {
    setLoading(true);
    try {
      const result = await studentService?.getStudents({
        programId: batchData?.programId,
        levelId: batchData?.levelId || undefined
      });

      if (result?.success) {
        setStudents(result?.data || []);
      }
    } catch (error) {
      console.error('Error loading students:', error);
      alert('Failed to load students: ' + error?.message);
    } finally {
      setLoading(false);
    }
  };

  const loadUnits = async () => {
    setLoading(true);
    try {
      const result = await enrollmentService?.getAvailableUnits(
        batchData?.programId,
        batchData?.levelId,
        semesterId
      );

      if (result?.success) {
        setUnits(result?.data || []);
      }
    } catch (error) {
      console.error('Error loading units:', error);
      alert('Failed to load units: ' + error?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleProcessBulkEnrollment = async () => {
    if (batchData?.studentIds?.length === 0 || batchData?.unitIds?.length === 0) {
      alert('Please select students and units');
      return;
    }

    setLoading(true);
    try {
      // Create batch
      const batchResult = await enrollmentService?.createBulkEnrollmentBatch({
        batchName: batchData?.batchName,
        academicYearId,
        semesterId,
        programId: batchData?.programId,
        levelId: batchData?.levelId,
        studentIds: batchData?.studentIds
      });

      if (!batchResult?.success) {
        throw new Error(batchResult.error);
      }

      // Process enrollments
      const processResult = await enrollmentService?.processBulkEnrollment(
        batchResult?.data?.id,
        batchData?.studentIds,
        batchData?.unitIds,
        academicYearId,
        semesterId
      );

      setProcessResult(processResult?.data);
      setStep(5); // Results step
    } catch (error) {
      console.error('Error processing bulk enrollment:', error);
      alert('Failed to process bulk enrollment: ' + error?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleStudentToggle = (studentId) => {
    setBatchData(prev => ({
      ...prev,
      studentIds: prev?.studentIds?.includes(studentId)
        ? prev?.studentIds?.filter(id => id !== studentId)
        : [...prev?.studentIds, studentId]
    }));
  };

  const handleUnitToggle = (unitId) => {
    setBatchData(prev => ({
      ...prev,
      unitIds: prev?.unitIds?.includes(unitId)
        ? prev?.unitIds?.filter(id => id !== unitId)
        : [...prev?.unitIds, unitId]
    }));
  };

  const handleSelectAllStudents = () => {
    setBatchData(prev => ({
      ...prev,
      studentIds: prev?.studentIds?.length === students?.length
        ? []
        : students?.map(s => s?.id)
    }));
  };

  const handleSelectAllUnits = () => {
    setBatchData(prev => ({
      ...prev,
      unitIds: prev?.unitIds?.length === units?.length
        ? []
        : units?.map(u => u?.id)
    }));
  };

  const renderStepContent = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Batch Name *
              </label>
              <input
                type="text"
                value={batchData?.batchName}
                onChange={(e) => setBatchData({ ...batchData, batchName: e?.target?.value })}
                placeholder="e.g., Semester 1 2025 Enrollment"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <Select
              label="Program *"
              value={batchData?.programId}
              onChange={(e) => setBatchData({ ...batchData, programId: e?.target?.value, levelId: '' })}
            >
              <option value="">Select Program</option>
              {programs?.map(program => (
                <option key={program?.id} value={program?.id}>
                  {program?.program_name}
                </option>
              ))}
            </Select>
            {levels?.length > 0 && (
              <Select
                label="Level (Optional)"
                value={batchData?.levelId}
                onChange={(e) => setBatchData({ ...batchData, levelId: e?.target?.value })}
              >
                <option value="">All Levels</option>
                {levels?.map(level => (
                  <option key={level?.id} value={level?.id}>
                    {level?.level_name}
                  </option>
                ))}
              </Select>
            )}
          </div>
        );

      case 2:
        return (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Select Students ({batchData?.studentIds?.length} selected)
              </h3>
              <Button
                onClick={handleSelectAllStudents}
                className="text-blue-600 hover:text-blue-700"
              >
                {batchData?.studentIds?.length === students?.length ? 'Deselect All' : 'Select All'}
              </Button>
            </div>
            <div className="max-h-96 overflow-y-auto space-y-2">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <span className="ml-3 text-gray-600">Loading students...</span>
                </div>
              ) : students?.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  No students found for the selected program
                </div>
              ) : (
                students?.map(student => (
                  <label
                    key={student?.profile_id || student?.id}
                    className="flex items-center p-3 border rounded-lg hover:bg-blue-50 cursor-pointer transition-colors"
                  >
                    <input
                      type="checkbox"
                      checked={batchData?.studentIds?.includes(student?.profile_id || student?.id)}
                      onChange={() => handleStudentToggle(student?.profile_id || student?.id)}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <div className="ml-3">
                      <div className="font-medium text-gray-900">{student?.full_name}</div>
                      <div className="text-sm text-gray-500">{student?.student_id}</div>
                    </div>
                  </label>
                ))
              )}
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Select Units ({batchData?.unitIds?.length} selected)
              </h3>
              <Button
                onClick={handleSelectAllUnits}
                className="text-blue-600 hover:text-blue-700"
              >
                {batchData?.unitIds?.length === units?.length ? 'Deselect All' : 'Select All'}
              </Button>
            </div>
            <div className="max-h-96 overflow-y-auto space-y-2">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <span className="ml-3 text-gray-600">Loading units...</span>
                </div>
              ) : units?.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  No units available for the selected program and level
                </div>
              ) : (
                units?.map(unit => (
                  <label
                    key={unit?.id}
                    className="flex items-start p-3 border rounded-lg hover:bg-blue-50 cursor-pointer transition-colors"
                  >
                    <input
                      type="checkbox"
                      checked={batchData?.unitIds?.includes(unit?.id)}
                      onChange={() => handleUnitToggle(unit?.id)}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500 mt-1"
                    />
                    <div className="ml-3 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900">
                          {unit?.unit_code} - {unit?.unit_name}
                        </span>
                        <span className="text-xs px-2 py-1 bg-purple-100 text-purple-800 rounded-full">
                          {unit?.credits} Credits
                        </span>
                      </div>
                      <div className="text-sm text-gray-500 mt-1">
                        Capacity: {unit?.current_enrollment || 0}/{unit?.max_capacity || 0}
                      </div>
                    </div>
                  </label>
                ))
              )}
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div className="bg-blue-50 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Review Enrollment Details</h3>
              
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Batch Name:</span>
                  <span className="font-medium text-gray-900">{batchData?.batchName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Program:</span>
                  <span className="font-medium text-gray-900">
                    {programs?.find(p => p?.id === batchData?.programId)?.program_name}
                  </span>
                </div>
                {batchData?.levelId && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Level:</span>
                    <span className="font-medium text-gray-900">
                      {levels?.find(l => l?.id === batchData?.levelId)?.level_name}
                    </span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-600">Number of Students:</span>
                  <span className="font-medium text-gray-900">{batchData?.studentIds?.length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Number of Units:</span>
                  <span className="font-medium text-gray-900">{batchData?.unitIds?.length}</span>
                </div>
                <div className="flex justify-between pt-3 border-t">
                  <span className="text-gray-600">Total Enrollments to Process:</span>
                  <span className="font-bold text-blue-600">
                    {batchData?.studentIds?.length * batchData?.unitIds?.length}
                  </span>
                </div>
              </div>
            </div>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-sm text-yellow-800">
                <strong>Note:</strong> This operation will attempt to enroll {batchData?.studentIds?.length} students 
                in {batchData?.unitIds?.length} units. Prerequisites and capacity will be validated for each enrollment. 
                Students who don't meet requirements will be added to waiting lists or skipped.
              </p>
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-6">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                Bulk Enrollment Completed
              </h3>
              <p className="text-gray-600">
                The enrollment process has finished. Review the results below.
              </p>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="bg-blue-50 rounded-lg p-6 text-center">
                <div className="text-3xl font-bold text-blue-600 mb-2">
                  {processResult?.total_processed || 0}
                </div>
                <div className="text-sm text-gray-600">Total Processed</div>
              </div>
              <div className="bg-green-50 rounded-lg p-6 text-center">
                <div className="text-3xl font-bold text-green-600 mb-2">
                  {processResult?.successful || 0}
                </div>
                <div className="text-sm text-gray-600">Successful</div>
              </div>
              <div className="bg-red-50 rounded-lg p-6 text-center">
                <div className="text-3xl font-bold text-red-600 mb-2">
                  {processResult?.failed || 0}
                </div>
                <div className="text-sm text-gray-600">Failed</div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const canProceed = () => {
    switch (step) {
      case 1:
        return batchData?.batchName && batchData?.programId;
      case 2:
        return batchData?.studentIds?.length > 0;
      case 3:
        return batchData?.unitIds?.length > 0;
      case 4:
        return true;
      default:
        return false;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-white">Bulk Enrollment</h2>
            <p className="text-purple-100 text-sm mt-1">
              {step === 5 ? 'Enrollment Complete' : `Step ${step} of 4`}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-white/20 rounded-full p-2 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Progress Bar */}
        {step < 5 && (
          <div className="bg-gray-100 h-2">
            <div
              className="bg-purple-600 h-full transition-all duration-300"
              style={{ width: `${(step / 4) * 100}%` }}
            />
          </div>
        )}

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          {renderStepContent()}
        </div>

        {/* Footer */}
        <div className="border-t bg-gray-50 px-6 py-4 flex items-center justify-between">
          <div>
            {step > 1 && step < 5 && (
              <Button
                onClick={() => setStep(step - 1)}
                className="px-6 py-2 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg"
                disabled={loading}
              >
                Back
              </Button>
            )}
          </div>
          <div className="flex gap-3">
            {step === 5 ? (
              <Button
                onClick={onClose}
                className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
              >
                Close
              </Button>
            ) : step === 4 ? (
              <Button
                onClick={handleProcessBulkEnrollment}
                disabled={loading || !canProceed()}
                className="px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Processing...' : 'Process Enrollment'}
              </Button>
            ) : (
              <Button
                onClick={() => setStep(step + 1)}
                disabled={!canProceed()}
                className="px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkEnrollmentModal;
